/**
 * Dashboard widget — "Facturi în reîncercare".
 *
 * Lists every invoice with status='retrying' (the ANAF retry queue's
 * in-flight set). For each, shows:
 *   - attempt counter (retry_count + 1 / ANAF_MAX_RETRIES)
 *   - countdown to next_retry_at (refreshes every second)
 *   - last_retry_error (truncated, click row to see full in detail page)
 *
 * Polls every 30s so a freshly-cron-processed invoice falls off without
 * a manual refresh. Hidden when the list is empty so the dashboard
 * stays uncluttered for users who never hit a transient ANAF failure.
 *
 * Why a dedicated widget (not just a filter):
 *   "retrying" is transient by design — users want to know "what's stuck
 *   right now and when will it clear" without scrolling through every
 *   invoice. Placing this above the stats grid puts the answer
 *   one glance away.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

// Mirrors supabase/functions/_shared/anafAdapter.ts — duplicated here
// (instead of imported) because that file is Deno-side and would pull
// the `declare const Deno` shim into the frontend bundle. The 5-step
// schedule is small and stable; both ends keeping it in lockstep is
// guarded by the unit tests in src/lib/anafAdapter.test.ts.
const ANAF_MAX_RETRIES = 5;

type RetryingInvoice = {
  id: string;
  invoice_number: string | null;
  buyer_name: string | null;
  retry_count: number;
  next_retry_at: string | null;
  last_retry_error: string | null;
};

function formatCountdown(target: string | null): string {
  if (!target) return "—";
  const ms = new Date(target).getTime() - Date.now();
  if (ms <= 0) return "în curând…";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

export default function RetryingInvoicesSection({ userId }: { userId: string | undefined }) {
  const navigate = useNavigate();

  // 1s tick drives the per-row countdown without re-querying the DB.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  // Refetch every 30s so cron-processed invoices drop off automatically.
  const { data: rows = [] } = useQuery({
    queryKey: ["invoices-retrying", userId],
    enabled: !!userId,
    refetchInterval: 30_000,
    staleTime: 15_000,
    queryFn: async (): Promise<RetryingInvoice[]> => {
      const { data, error } = await supabase
        .from("invoices")
        .select("id, invoice_number, buyer_name, retry_count, next_retry_at, last_retry_error")
        .eq("status", "retrying")
        .order("next_retry_at", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return (data ?? []) as RetryingInvoice[];
    },
  });

  if (rows.length === 0) return null;

  return (
    <section
      aria-label="Facturi în reîncercare la ANAF"
      className="rounded-xl border border-primary/20 bg-primary/5 p-4 mb-6"
    >
      <header className="flex items-center gap-2 mb-3">
        <RefreshCw className="h-4 w-4 text-primary animate-spin" />
        <h2 className="font-heading font-semibold text-sm">
          Facturi în reîncercare la ANAF
        </h2>
        <Badge variant="info" className="ml-auto text-xs">
          {rows.length}
        </Badge>
      </header>

      <ul className="space-y-2">
        {rows.map((inv) => {
          const attempt = Math.min(inv.retry_count + 1, ANAF_MAX_RETRIES);
          return (
            <li key={inv.id}>
              <button
                onClick={() => navigate(`/invoice/${inv.id}`)}
                className="w-full text-left rounded-lg border border-border bg-card p-3 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">
                      {inv.buyer_name || "Factură"}
                      <span className="text-muted-foreground font-normal ml-2">
                        {inv.invoice_number || "fără număr"}
                      </span>
                    </div>
                    {inv.last_retry_error && (
                      <div className="mt-1 flex items-start gap-1 text-xs text-muted-foreground">
                        <AlertCircle className="h-3 w-3 shrink-0 mt-0.5 text-warning" />
                        <span className="truncate">{inv.last_retry_error}</span>
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-xs text-muted-foreground">
                      Încercarea <span className="font-semibold text-foreground">{attempt}/{ANAF_MAX_RETRIES}</span>
                    </div>
                    <div className="text-xs flex items-center justify-end gap-1 mt-0.5">
                      <Loader2 className="h-3 w-3 animate-spin text-primary" />
                      <span className="font-mono tabular-nums">
                        {formatCountdown(inv.next_retry_at)}
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
